import type { VisibilityState } from '@tanstack/react-table';
import type {
  CertificationRecord,
  CompetencyTrendRecord,
  EmployeeTrainingSummary,
  Tier1TrendRecord,
} from '../../../types/driInsights';
import { exportCsvWithVisibleColumns } from '../../../utils/csvExport';

const EMPLOYEE_TRAININGS_SUMMARY_COLUMN_ORDER = [
  'employee_email',
  'registration_status',
  'total_certifications',
  'total_skill_badges',
  'dri_tier',
  'dri_score_avg',
  'dri_score_highest',
  'competencies',
  'tier1_products',
  'certifications_list',
] as const;

const EMPLOYEE_TRAININGS_SUMMARY_CSV_LABELS: Record<string, string> = {
  employee_email: 'Employee Email',
  registration_status: 'Status',
  total_certifications: 'Total Certifications',
  total_skill_badges: 'Total Skill Badges',
  dri_tier: 'DRI Tier',
  dri_score_avg: 'DRI Score Avg',
  dri_score_highest: 'DRI Score Highest',
  competencies: 'Competencies',
  tier1_products: 'Tier1 products',
  certifications_list: 'Certifications List',
};

function isRegistered(row: EmployeeTrainingSummary): boolean {
  return row.is_registered !== false;
}

function competencyNamesForRow(
  row: EmployeeTrainingSummary,
  competencyTrendData: CompetencyTrendRecord[]
): string {
  if (!isRegistered(row)) return '-';
  const withDate = (competencyTrendData || []).filter(
    (r) =>
      (r.email ?? '') === row.employee_email &&
      r.competency_achieved_date != null &&
      r.competency_achieved_date !== ''
  );
  const names = Array.from(new Set(withDate.map((r) => r.competency).filter((c): c is string => Boolean(c))));
  return names.length > 0 ? names.join('; ') : '—';
}

function tier1ProductsForRow(row: EmployeeTrainingSummary, tier1TrendData: Tier1TrendRecord[]): string {
  if (!isRegistered(row)) return '-';
  const withDate = (tier1TrendData || []).filter(
    (r) =>
      (r.email ?? '') === row.employee_email &&
      r.tier1_accomplished_date != null &&
      r.tier1_accomplished_date !== ''
  );
  const products = Array.from(new Set(withDate.map((r) => r.gcp_product).filter((p): p is string => Boolean(p))));
  return products.length > 0 ? products.join('; ') : '—';
}

function certificationsListForRow(
  row: EmployeeTrainingSummary,
  getCertificationsForEmployee: (email: string) => CertificationRecord[]
): string {
  const certs = getCertificationsForEmployee(row.employee_email);
  if (!certs.length) return '';
  return certs
    .map((c) => c.certification_name)
    .filter(Boolean)
    .join('; ');
}

function getTrainingSummaryCellCsv(
  row: EmployeeTrainingSummary,
  columnId: string,
  ctx: {
    getCertificationsForEmployee: (email: string) => CertificationRecord[];
    tier1TrendData: Tier1TrendRecord[];
    competencyTrendData: CompetencyTrendRecord[];
  }
): string {
  switch (columnId) {
    case 'employee_email':
      return row.employee_email ?? '';
    case 'registration_status':
      return isRegistered(row) ? 'Registered' : 'Unregistered';
    case 'total_certifications':
      return String(row.total_certifications ?? 0);
    case 'total_skill_badges':
      return String(row.total_skill_badges ?? 0);
    case 'dri_tier':
      if (!isRegistered(row)) return '-';
      return `Tier ${row.dri_tier}`;
    case 'dri_score_avg':
      if (!isRegistered(row)) return '-';
      return row.dri_score_avg != null ? Number(row.dri_score_avg).toFixed(2) : '';
    case 'dri_score_highest':
      if (!isRegistered(row)) return '-';
      return row.dri_score_highest != null ? Number(row.dri_score_highest).toFixed(2) : '';
    case 'competencies':
      return competencyNamesForRow(row, ctx.competencyTrendData);
    case 'tier1_products':
      return tier1ProductsForRow(row, ctx.tier1TrendData);
    case 'certifications_list':
      return certificationsListForRow(row, ctx.getCertificationsForEmployee);
    default:
      return '';
  }
}

/** All filtered rows (not paginated), columns per Toggle Columns visibility. */
export function exportEmployeeTrainingsSummaryToCsv(options: {
  rows: EmployeeTrainingSummary[];
  columnVisibility: VisibilityState;
  getCertificationsForEmployee: (email: string) => CertificationRecord[];
  tier1TrendData?: Tier1TrendRecord[];
  competencyTrendData?: CompetencyTrendRecord[];
  filenameBase?: string;
}): void {
  const {
    rows,
    columnVisibility,
    getCertificationsForEmployee,
    tier1TrendData = [],
    competencyTrendData = [],
    filenameBase = 'dri-insights-employee-summary',
  } = options;
  if (!rows.length) return;

  const ctx = { getCertificationsForEmployee, tier1TrendData, competencyTrendData };
  exportCsvWithVisibleColumns({
    rows,
    columnVisibility,
    columnIdsInOrder: EMPLOYEE_TRAININGS_SUMMARY_COLUMN_ORDER,
    columnLabels: EMPLOYEE_TRAININGS_SUMMARY_CSV_LABELS,
    getCellText: (row, columnId) => getTrainingSummaryCellCsv(row, columnId, ctx),
    filenameBase,
  });
}
